// Modal de onboarding con carrusel de pasos
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { OnboardingStep } from './OnboardingStep';
import { onboardingSteps } from './onboardingSteps';

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  onDismiss: () => void;
}

export function OnboardingModal({ open, onClose, onDismiss }: OnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const isFirst = currentStep === 0;
  const isLast = currentStep === onboardingSteps.length - 1;

  const handleClose = () => {
    if (dontShowAgain) {
      onDismiss();
    }
    onClose();
    // Resetear al cerrar para la próxima apertura
    setCurrentStep(0);
  };

  const handleNext = () => {
    if (isLast) {
      handleClose();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bienvenido a ProcesoFácil</DialogTitle>
          <DialogDescription>
            Paso {currentStep + 1} de {onboardingSteps.length}
          </DialogDescription>
        </DialogHeader>

        {/* Contenido del paso actual */}
        <div className="py-4 min-h-[280px] flex items-center justify-center">
          <OnboardingStep step={onboardingSteps[currentStep]} />
        </div>

        {/* Indicadores de paso (dots) */}
        <div className="flex justify-center gap-2">
          {onboardingSteps.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentStep
                  ? 'w-6 bg-emerald-600 dark:bg-emerald-400'
                  : 'w-2 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
              }`}
              aria-label={`Ir al paso ${index + 1}`}
            />
          ))}
        </div>

        {/* Footer: checkbox + botones */}
        <div className="flex items-center justify-between pt-2">
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            No volver a mostrar
          </label>

          <div className="flex gap-2">
            {!isFirst && (
              <Button variant="outline" onClick={handlePrev}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
            )}
            <Button onClick={handleNext}>
              {isLast ? 'Comenzar' : (
                <>
                  Siguiente
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
